/* eslint-disable max-lines */
import snakeCaseKeys from 'snakecase-keys';
import _ from 'lodash';
import { orderStatus } from '../constants';
import {
  getLineItemsForOrder,
  getUserId,
  mapDataWithLenghtsToDataWithColors,
} from '../selectors';
import { saveSalesforceOrder } from '../Api/salesforceOrders/saveSalesforceOrder';
import { getTranslateByScope } from '../i18n';
import { routes } from '../routes';
import { getSalesforceConnection } from './getSalesforceConnection';
import { isUnauthorized } from './salesforce';

const translate = getTranslateByScope('app.utils.order');

const genId = (prefix, length) => {
  let result = '';
  const characters = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const charactersLength = characters.length;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  if (prefix === undefined || prefix === null || prefix.length === 0) {
    // eslint-disable-next-line no-param-reassign
    prefix = 'SCH';
  }

  return prefix + result;
};

export const generateOrderId = (prefix) => genId(prefix, 6);

export const getProductsFromOrder = (order) => {
  if (!order || !order.products) return [];
  const products = _.values(order.products);

  return _.reverse(_.sortBy(products, ['createdAt']));
};

const orderHasNoProducts = (order) => !order || !order.products;

export const addOrUpdateOrderProduct = ({ order, product }) => {
  if (!product) return order;

  const orderAfterUpdate = { ...order };
  if (orderHasNoProducts(orderAfterUpdate)) {
    orderAfterUpdate.products = {};
  }
  const productToUpdate = orderAfterUpdate.products[product.id] || {};
  orderAfterUpdate.products = { ...orderAfterUpdate.products };
  orderAfterUpdate.products[product.id] = {
    ...productToUpdate,
    ...product,
  };

  return orderAfterUpdate;
};

const mapProducts = (products) => {
  if (!products) return {};
  const mapped = {};
  products.forEach((p) => {
    mapped[p.id] = p;
  });

  return mapped;
};

export const addMultipleProductsToOrder = ({ order, products }) => {
  if (!products || !order) return order;
  const orderAfterUpdate = { ...order };
  if (orderHasNoProducts(order)) {
    orderAfterUpdate.products = {};
  }

  const nextProducts = {
    ...orderAfterUpdate.products,
    ...mapProducts(products),
  };
  return { ...orderAfterUpdate, products: nextProducts };
};

export const removeProductFromOrder = ({ products, id }) => {
  if (!products) return {};
  const productsToRemove = { ...products };
  delete productsToRemove[id];
  return productsToRemove;
};

const mapOrderDiscount = (discount) => (discount || 0) / 100;
const mapOrderType = (order) =>
  order.orderType ? order.orderType.value : null;
const mapAddress = (order, addressParam) => {
  if (!order[addressParam]) return null;

  const address = order[addressParam];

  return {
    name1: address.name,
    name2: address.name2,
    street: address.street + (address.number ? ` ${address.number}` : ''),
    city: address.city,
    state: address.state,
    country: address.country,
    postalcode: address.postalCode,
    shipToCode: address.shipToCode,
  };
};

const mapLineItems = (lineItems) => {
  if (!lineItems) return null;
  const lineItemsMapped = lineItems.map((l) => ({
    maskLineId: _.get(l, 'mask_line_id'),
    quantity: _.get(l, 'quantity'),
    season: _.get(l, 'season'),
    ean: _.get(l, 'ean'),
    deliveryPeriodCode: _.get(l, 'deliveryPeriodCode'),
    item_no: _.get(l, 'number'),
    price: _.get(l, 'price.value') || 0,
  }));

  return lineItemsMapped;
  // return _.filter(lineItemsMapped, l => l.price);
};

export const mapToOrder = (order) => {
  if (!order) return null;

  return {
    order: snakeCaseKeys({
      orderId: order.id,
      customerId: order.customerId,
      discount: mapOrderDiscount(order.discount),
      discount1: mapOrderDiscount(order.discount1),
      discount2: mapOrderDiscount(order.discount2),
      discount3: mapOrderDiscount(order.discount3),
      orderType: mapOrderType(order),
      orderDate: order.orderDate,
      minDeliveryDate: order.minDeliveryDate,
      billingAddress: mapAddress(order, 'billingAddress'),
      shippingAddress: mapAddress(order, 'deliveryAddress'),
      comment: order.notes,
      lineItems: mapLineItems(_.flatten(order.lineItems)),
      externalOrderId: order.orderNumber,
      agentId: order.agentId,
      userId: order.userId,
      comments: order.comments,
    }),
  };
};

export const isOrderEditable = (order) => {
  return !!order && !!order.status && (
    order.status === orderStatus.draft ||
    order.status === orderStatus.unsynced
  )
}

export const hasOrderFailedToSync = (order) =>
  !!order && order.status === orderStatus.failedToSync;

export const isOrderDone = (order) =>
  !!order && order.status === orderStatus.done;

const getQuantity = (quantities) =>
  _.reduce(Object.values(quantities), (sum, q) => sum + q) || 0;

const checkIfProductHasMissingQuantities = (p) => {
  let hasProductWithMissingQuantities = true;
  _.keys(p.data).forEach((colorCode) => {
    const quantities = p.data[colorCode];
    const quantity = getQuantity(quantities) || 0;
    if (quantity) hasProductWithMissingQuantities = false;
  });
  return hasProductWithMissingQuantities;
};

export const mapNotesToOrder = (orderNotes) =>
  orderNotes
    ? orderNotes.reduce(
        (total, orderNote) => ({
          ...total,
          [orderNote.id]: {
            nr: orderNote.number,
            comments: orderNote.notes,
          },
        }),
        {}
      )
    : undefined;

export const handleSubmitOrder = (
  orderToSave,
  context,
  history,
  onSuccess,
  onFailure
) => {
  const mappedOrder = normalizeOrder(orderToSave, context, onFailure);
  if (!mappedOrder) {
    onFailure();
    return;
  }
  const { order: orderTemp } = mappedOrder;

  const flaggedOrder = {
    ...mappedOrder,
    order: { ...orderTemp, draft: false },
  };
  saveSalesforceOrder(flaggedOrder)
    .then(() => {
      context.actions.setToast({
        title: translate('toast.title'),
        description: translate('toast.description'),
      });

      const statusDoneOrder = {
        ...orderToSave,
        doc: { ...orderToSave.doc, status: orderStatus.done },
      };

      context.actions.closeOrder(statusDoneOrder.id, {
        ...statusDoneOrder.doc,
      });
      if (history) {
        history.push(routes.orderConfirmation);
      }

      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      context.actions.setToast({
        title: translate('toast.fail'),
        text: translate('toast.failDescription'),
      });
      context.actions.closeOrderButFailedToSync(orderToSave.id, {
        ...orderToSave.doc,
      });

      if (onFailure) {
        onFailure();
      }
    });
};

  const deleteSalesforceOrder = async (context, orderId) => {
    let retries = 0;

    const inner = async () => {
      if (retries === 5) {
        throw new Error('Max out retries');
      }
      try {
        const conn = await getSalesforceConnection(
          context.entities.session,
          retries > 0
        );
        let url = '/orders/delete_order?order_id=' + orderId;
        console.log('deleting i think')
        const data = await conn.apex.post(url, '{}');
        return data;
      } catch (error) {
        if (isUnauthorized(error)) {
          retries++;

          const data = await inner();
          return data;
        }
      }
    };

    const data = await inner();

    return data;
  };

export const deleteDraftOrder = (
  orderToDelete,
  context,
) => {
  deleteSalesforceOrder(context, orderToDelete.doc_id);
}

export const handleSubmitDraftOrder = (
  orderToSave,
  context,
  onSuccess,
  onFailure
) => {
  const mappedOrder = normalizeOrder(orderToSave, context, onFailure);
  if (!mappedOrder) {
    onFailure();
    return;
  }
  const { order } = mappedOrder;
  const flaggedOrder = {
    ...mappedOrder,
    order: { ...order, draft: true },
  };

  const statusDraftOrder = {
    ...orderToSave,
    doc: { ...orderToSave.doc, status: orderStatus.draft },
  };

  saveSalesforceOrder(flaggedOrder)
    .then(() => {
      context.actions.closeOrder(statusDraftOrder.id, {
        ...statusDraftOrder.doc,
      });

      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(() => {
      context.actions.setToast({
        title: translate('saveAsDraftToast.fail'),
        text: translate('saveAsDraftToast.failDescription'),
      });
      context.actions.closeOrder(statusDraftOrder.id, {
        ...statusDraftOrder.doc,
      }, true);

      if (onFailure) {
        onFailure();
      }
    });
};

const normalizeOrder = (orderToSave, context, onFailure) => {
  if (!orderToSave) return;

  let order = orderToSave;

  if (orderToSave.doc) {
    order = { ...orderToSave.doc };
  }
  const allNotesForProducts = [];

  const lineItems = [];

  let hasProductWithMissingQuantities = true;

  _.values(order.products).forEach((p) => {
    if (!p.id) return;
    const product = {
      ...p,
      data: mapDataWithLenghtsToDataWithColors(p.data),
    };

    hasProductWithMissingQuantities =
      checkIfProductHasMissingQuantities(product);
    if (!hasProductWithMissingQuantities) {
      lineItems.push(getLineItemsForOrder(product, context));
    }

    if (p.notes) {
      allNotesForProducts.push({ notes: p.notes, id: p.id, number: p.number });
    }
  });

  const hasOrderType = !_.isEmpty(order.orderType);
  if (!hasOrderType) {
    context.actions.setToast({
      title: translate('missingOrderType.title'),
      text: translate('missingOrderType.text'),
    });
    onFailure();
    return;
  }

  const hasDeliveryAddress = !_.isEmpty(order.deliveryAddress);
  if (!hasDeliveryAddress) {
    context.actions.setToast({
      title: translate('missingDeliveryAddress.title'),
      text: translate('missingDeliveryAddress.text'),
    });
    onFailure();
    return;
  }

  delete order.products;

  if (hasProductWithMissingQuantities) {
    context.actions.setToast({
      title: translate('missingQuantitiesInProduct.title'),
      text: translate('missingQuantitiesInProduct.text'),
    });
    onFailure();
    return;
  }

  const mappedOrder = mapToOrder({
    lineItems,
    ...order,
    agentId: getUserId(context),
    comments: mapNotesToOrder(allNotesForProducts),
  });
  const orderWithSeason = {
    order: { ...mappedOrder.order, season: orderToSave.doc.season },
  };

  return orderWithSeason;
};
